import { createFileRoute, useSearch } from '@tanstack/react-router'
import type { JSX } from 'react'
import { createPortal } from 'react-dom'

import { useDisplaySession } from '../components/display-runtime.tsx'
import { useDisplayPreview } from '../components/display-preview-context.tsx'
import { TV_PAGE_HEIGHT } from '../components/tv-canvas.tsx'
import { isDisplayActive } from '../lib/display-mode.ts'
import { useInertAppRoot } from '../hooks/use-inert-app-root.ts'

import { YOUTUBE_VIDEO_ID } from '../lib/youtube-video.ts'

/**
 * The TV canvas gives a page no fixed height, so `h-full` falls back to the
 * iframe's default height. The canvas publishes the room in this property
 * instead.
 */
const TV_HEIGHT = `var(${TV_PAGE_HEIGHT}, 100vh)`

/** A validated YouTube video loop for the office TV. */
export const Route = createFileRoute('/display-video')({
  validateSearch: (
    search: Record<string, unknown>
  ): { id?: string; sound?: 1 } => ({
    ...(typeof search.id === 'string' && YOUTUBE_VIDEO_ID.test(search.id)
      ? { id: search.id }
      : {}),
    ...(search.sound === 1 || search.sound === '1' ? { sound: 1 as const } : {})
  }),
  component: VideoScreen
})

/**
 * The video fills the TV canvas inside a display. Anywhere else it covers the
 * console through a portal on the page body, as the office board does.
 *
 * @returns The video.
 */
function VideoScreen(): JSX.Element {
  const search: Record<string, unknown> = useSearch({ strict: false })
  const id =
    typeof search.id === 'string' && YOUTUBE_VIDEO_ID.test(search.id)
      ? search.id
      : null
  const preview = useDisplayPreview()
  const mute = search.sound === 1 && !preview ? '0' : '1'
  // YouTube loops one video only when playlist repeats the same id.
  const src =
    id === null
      ? null
      : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=${mute}&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&playsinline=1&disablekb=1&iv_load_policy=3`
  const displayActive = isDisplayActive(useDisplaySession()) || preview
  useInertAppRoot(!displayActive)

  const screen = (
    <div
      data-testid="display-video-screen"
      className={
        displayActive
          ? 'bg-ink-deep w-full overflow-hidden'
          : 'bg-ink-deep fixed inset-0 z-50 overflow-hidden'
      }
      style={displayActive ? { height: TV_HEIGHT } : undefined}
    >
      {src === null ? (
        <div
          data-testid="display-video-empty"
          className="grid h-full min-h-svh place-items-center text-muted-foreground"
        >
          No video
        </div>
      ) : (
        <iframe
          data-testid="display-video-frame"
          className="block h-full w-full border-0"
          src={src}
          title="YouTube video"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          // The player needs its scripts and its own origin's storage, or it shows an error.
          // The pair is safe here: the frame is youtube-nocookie.com, so it gets that
          // origin and never the console's.
          // oxlint-disable-next-line react/iframe-missing-sandbox
          sandbox="allow-scripts allow-same-origin allow-presentation"
        />
      )}
    </div>
  )
  return displayActive ? screen : createPortal(screen, document.body)
}
